import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { IssuedApiCredentialMaterial } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';

interface CredentialRotateDialogProps {
  open: boolean;
  credentialId: string;
  onClose: () => void;
  /** Fires with the freshly generated once-only `secret`/`signingSecret` pair (D-N). */
  onRotated: (credential: IssuedApiCredentialMaterial) => void;
}

/** `POST /v1/admin/api-credentials/{id}/rotate` — a confirmation gate before the once-only reveal. */
export function CredentialRotateDialog({ open, credentialId, onClose, onRotated }: CredentialRotateDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm() {
    setSubmitting(true);
    try {
      const credential = await payApiClient.rotateAdminApiCredential(credentialId);
      onRotated(credential);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.credentials.rotateDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Alert severity="warning">
            <Typography variant="body2">{translate('pay.credentials.rotateDialog.body')}</Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.credentials.rotateDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" color="warning" onClick={handleConfirm} disabled={submitting}>
            {translate('pay.credentials.rotateDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
